import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Layout from '@/pages/Frontend/Layout';
import DashboardLayout from '@/pages/Admin/DashboardLayout';
import NotFound from '@/pages/NotFound';
import Home from '@/pages/Admin/Home';
import Vouchers from '@/pages/Admin/Vouchers';
import Transactions from '@/pages/Admin/Transactions';
import Payout from '@/pages/Admin/Payout';
import Stores from '@/pages/Admin/Stores';
import Subscription from '@/pages/Admin/Subscription';

import Developers from '@/pages/Admin/Developers';
import Apps from '@/pages/Admin/Integration';
import Customization from '@/pages/Admin/Customization';
import { getProfile } from '@/api/auth';
import PublicRoute from '@/components/PublicRoute';
import PrivateRoute from '@/components/PrivateRoute';
import AdminRoute from '@/components/AdminRoute';
import StaffTabRoute from '@/components/StaffTabRoute';
import { CommunityRoute } from '@/features/community/CommunityDock';
import { WIFI_PANEL_TABS } from '@/constants/staffTabs';
import { Skeleton, SkeletonLines } from '@/components/Admin/UI/Skeleton';
import UpdatePassword from '@/pages/Frontend/UpdatePassword';
import BillingSuccess from '@/pages/Admin/Billing/Success';
import BillingFailure from '@/pages/Admin/Billing/Failure';
import Billing from '@/pages/Admin/Billing';
import ApiIntegration from '@/pages/Admin/ApiIntegration';
// The admin panels are the heaviest screens in the app and only platform
// staff open them. Split out so an operator's dashboard never downloads or
// compiles them (2026-09-28: one 4.5 MB script for everyone).
const AdminPanel = lazy(() => import('@/pages/Admin/AdminPanel'));
const AdminPanelVendo = lazy(() => import('@/pages/Admin/AdminPanelVendo'));
const AdminPanelPoints = lazy(() => import('@/pages/Admin/AdminPanelPoints'));
const AdminPanelIsp = lazy(() => import('@/pages/Admin/AdminPanelIsp'));
import IspApply from '@/pages/Isp/IspApply';
import IspPortal from '@/pages/Isp/IspPortal';
// The Posts screen carries the block editor, which is far too big to sit in
// the bundle every operator downloads. Loaded on demand instead.
const AdminPanelPosts = lazy(() => import('@/pages/Admin/AdminPanelPosts'));
const AdminPanelShop = lazy(() => import('@/pages/Admin/AdminPanelShop'));

/** The shape of an admin screen — title, a row of cards, a table — for the
 *  moment its code is downloading. Grey where the content will be, so the
 *  page does not jump when it arrives. */
const PanelLoading: React.FC = () => (
   <div style={{ padding: '1.5rem 2rem' }} aria-busy="true" aria-label="Loading">
      <Skeleton $block $w="220px" $h="22px" style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
         {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} $block $w="25%" $h="84px" $radius="12px" />
         ))}
      </div>
      <SkeletonLines lines={8} />
   </div>
);
import DataPlans from '@/pages/Admin/DataPlans';
import QrSticker from '@/pages/Admin/QrSticker';
import Partners from '@/pages/Admin/Partners';
import Distributor from '@/pages/Admin/Distributor';
import Reseller from '@/pages/Admin/Reseller';
import WifiDashboard from '@/pages/Admin/Wifi/Dashboard';
import IspHome from '@/pages/Admin/Isp/IspHome';
import IspPlans from '@/pages/Admin/Isp/IspPlans';
import IspAccounts from '@/pages/Admin/Isp/IspAccounts';
import IspInvoices from '@/pages/Admin/Isp/IspInvoices';
import IspSmartfiBilling from '@/pages/Admin/Isp/IspSmartfiBilling';
import { ISP_ENABLED } from '@/pages/Admin/Isp/ispEnabled';
import IspComingSoon from '@/pages/Admin/Isp/IspComingSoon';
import IspCustomers from '@/pages/Admin/Isp/IspCustomers';
import IspServers from '@/pages/Admin/Isp/IspServers';
import IspSetup from '@/pages/Admin/Isp/IspSetup';
import WifiDevices from '@/pages/Admin/Wifi/Devices';
import WifiNetworks from '@/pages/Admin/Wifi/Networks';
import WifiSoldDevices from '@/pages/Admin/Wifi/SoldDevices';
import WifiOwnershipRequests from '@/pages/Admin/Wifi/OwnershipRequests';
import TransferHandoff from '@/pages/TransferHandoff';
import TeamAccept from '@/pages/TeamAccept';
import Team from '@/pages/Admin/Team';
import SellerPage from '@/pages/SellerPage';
import LegacySellerLink from '@/routes/LegacySellerLink';
import WifiSessions from '@/pages/Admin/Wifi/Sessions';
import WifiSettings from '@/pages/Admin/Wifi/Settings';
import WifiVouchers from '@/pages/Admin/WifiVouchers';
import WifiVoucherPayout from '@/pages/Admin/WifiVoucherPayout';
import SurfaceRedirect from '@/pages/Admin/SurfaceRedirect';
// import UpdatePassword from '@/pages/Frontend/UpdatePassword';

interface ProtectedRouteProps {
   children: React.ReactNode;
}

// const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
//    const [isAuthenticated, setIsAuthenticated] = useState(false);
//    const [isLoading, setIsLoading] = useState(true);

//    useEffect(() => {
//       const checkAuth = async () => {
//          const token = localStorage.getItem('x-token');

//          if (!token) {
//             setIsAuthenticated(false);
//             setIsLoading(false);
//             return;
//          }

//          try {
//             const response = await getProfile();
//             setIsAuthenticated(response.status === 200);
//          } catch (err) {
//             setIsAuthenticated(false);
//          } finally {
//             setIsLoading(false);
//          }
//       };

//       checkAuth();
//    }, []);

//    if (isLoading) {
//       return <div>Loading...</div>; // Or your loading component
//    }

//    if (!isAuthenticated) {
//       return <Navigate to="/" replace />;
//    }

//    return <>{children}</>;
// };

// const PublicRoute = ({ children }: ProtectedRouteProps) => {
//    const [isValidating, setIsValidating] = useState(true);
//    const [isAuthenticated, setIsAuthenticated] = useState(false);
//    const token = localStorage.getItem('x-token');

//    useEffect(() => {
//       const validateToken = async () => {
//          if (!token) {
//             setIsAuthenticated(false);
//             setIsValidating(false);
//             return;
//          }

//          try {
//             const response = await getProfile();
//             setIsAuthenticated(response.status === 200);
//          } catch (err) {
//             setIsAuthenticated(false);
//             localStorage.removeItem('x-token');
//          } finally {
//             setIsValidating(false);
//          }
//       };

//       validateToken();
//    }, [token]);

//    if (isValidating) {
//       return <div>Loading...</div>; // Or your loading component
//    }

//    if (isAuthenticated) {
//       return <Navigate to="/admin" replace />;
//    }

//    return <>{children}</>;
// };

const RoutesIndex: React.FC = () => {
   // Use useLocation hook to get the current route
   const location = useLocation();
   const currentRoute = location.pathname; // Get current path

   // Referral capture (points phase 3): a referral link is /?ref=<code>. The
   // ?ref param only lives on the landing URL — once the visitor toggles to the
   // sign-up form (same route, no query) it's gone. Persist it the moment we
   // see it so SignUp can read it back at submit time.
   useEffect(() => {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) {
         try {
            localStorage.setItem('smartfi_ref', ref);
         } catch {
            /* ignore storage errors (private mode) */
         }
      }
   }, []);

   return (
      <Routes>
         {/* <Route path="/" element={<Layout />} /> */}

         <Route element={<PublicRoute />}>
            <Route path="/" element={<Layout />} />
            <Route
               path="/update-password"
               element={<UpdatePassword handleForm={() => {}} />}
            />
         </Route>

         {/* Public + auth-tolerant: the transfer handoff renders for both
             signed-in and signed-out users — its UI branches on the
             token + current session. Don't wrap in PublicRoute (which
             would bounce authed users) or PrivateRoute (which would
             bounce new buyers). */}
         <Route path="/transfers/handoff" element={<TransferHandoff />} />

         {/* Team invite link — public + auth-tolerant like the handoff. */}
         <Route path="/team/accept" element={<TeamAccept />} />

         {/* Public + auth-tolerant, same reasoning as the handoff above: a
             shopper arriving from a partner's Facebook link is a stranger, and
             PublicRoute would bounce them once signed in. See SellerPage. */}
         <Route path="/s/:slug" element={<SellerPage />} />

         {/* The operator's public sign-up page. No login and no PublicRoute:
             an applicant is a stranger, and PublicRoute would bounce an
             operator who opened their own link while signed in — which is
             exactly how they check it works. Behind ISP_ENABLED like the rest
             of the product. */}
         {ISP_ENABLED && (
            <Route path="/isp/:slug/apply" element={<IspApply />} />
         )}

         {/* The subscriber's own bill. Public for the same reason: the person
             looking at it has no SmartFi account and never will. */}
         {ISP_ENABLED && (
            <Route path="/isp/:slug/bill" element={<IspPortal />} />
         )}

         {/* Post-login smart landing — picks surface, redirects. */}
         <Route element={<PrivateRoute />}>
            <Route path="/home" element={<SurfaceRedirect />} />
         </Route>

         {/* Voucher Store surface (new /voucher-* URLs). */}
         <Route element={<PrivateRoute />}>
            <Route
               path="/voucher"
               element={
                  <DashboardLayout active={currentRoute} children={<Home />} />
               }
            />
            <Route
               path="/voucher-stores"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Stores />}
                  />
               }
            />
            <Route
               path="/voucher-vouchers"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Vouchers />}
                  />
               }
            />
            <Route
               path="/voucher-transactions"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Transactions />}
                  />
               }
            />
            <Route
               path="/voucher-payout"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Payout />}
                  />
               }
            />
         </Route>

         {/* Legacy Voucher URL redirects — bookmarks keep working. */}
         <Route path="/admin" element={<Navigate to="/voucher" replace />} />
         <Route path="/stores" element={<Navigate to="/voucher-stores" replace />} />
         <Route path="/vouchers" element={<Navigate to="/voucher-vouchers" replace />} />
         <Route path="/transactions" element={<Navigate to="/voucher-transactions" replace />} />
         <Route path="/payout" element={<Navigate to="/voucher-payout" replace />} />

         <Route element={<PrivateRoute />}>
            <Route
               path="/subscription"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Subscription />}
                  />
               }
            />
         </Route>
         <Route element={<PrivateRoute />}>
            <Route
               path="/customization"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Customization />}
                  />
               }
            />
         </Route>
         <Route element={<AdminRoute />}>
            <Route
               path="/admin-panel-voucher"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={
                        <Suspense fallback={<PanelLoading />}>
                           <AdminPanel />
                        </Suspense>
                     }
                  />
               }
            />
            {/* Same rule as the menu entry: holding a Shop or Posts grant is
                not a reason to be let into the Piso WiFi panel. */}
            <Route element={<StaffTabRoute tab={WIFI_PANEL_TABS} />}>
               <Route
                  path="/admin-panel-wifi"
                  element={
                     <DashboardLayout
                        active={currentRoute}
                        children={
                        <Suspense fallback={<PanelLoading />}>
                           <AdminPanelVendo />
                        </Suspense>
                     }
                     />
                  }
               />
            </Route>
            <Route
               path="/admin-panel-points"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={
                        <Suspense fallback={<PanelLoading />}>
                           <AdminPanelPoints />
                        </Suspense>
                     }
                  />
               }
            />
            {/* ISP Business is a separate product, so its price list gets its
                own address rather than a section inside the piso panel. Still
                behind the same dark-launch flag as the rest of ISP — an admin
                who can reach this URL in a build where ISP is off would be
                editing prices for a product nobody can buy. */}
            {ISP_ENABLED && (
               <Route
                  path="/admin-panel-isp"
                  element={
                     <DashboardLayout
                        active={currentRoute}
                        children={
                        <Suspense fallback={<PanelLoading />}>
                           <AdminPanelIsp />
                        </Suspense>
                     }
                     />
                  }
               />
            )}
            {/* Posts has its own address, so it needs its own check — being
                staff is not enough, the Posts grant is. */}
            <Route element={<StaffTabRoute tab="posts" />}>
               <Route
                  path="/admin-panel-posts"
                  element={
                     <DashboardLayout
                        active={currentRoute}
                        children={
                           <Suspense fallback={<PanelLoading />}>
                              <AdminPanelPosts />
                           </Suspense>
                        }
                     />
                  }
               />
            </Route>
            <Route
               path="/admin-panel-shop"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={
                        <Suspense fallback={<PanelLoading />}>
                           <AdminPanelShop />
                        </Suspense>
                     }
                  />
               }
            />
         </Route>

         {/* Legacy admin-panel URL redirects. /admin-panel-firmware folded
             into the WiFi admin panel as a tab; no separate route. */}
         <Route path="/admin-panel" element={<Navigate to="/admin-panel-voucher" replace />} />
         <Route path="/admin-panel-vendo" element={<Navigate to="/admin-panel-wifi" replace />} />
         <Route path="/admin-panel-firmware" element={<Navigate to="/admin-panel-wifi" replace />} />

         <Route element={<PrivateRoute />}>
            <Route
               path="/api-integration"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<ApiIntegration />}
                  />
               }
            />
         </Route>

         <Route element={<PrivateRoute />}>
            <Route
               path="/apps"
               element={
                  <DashboardLayout active={currentRoute} children={<Apps />} />
               }
            />
         </Route>

         <Route element={<PrivateRoute />}>
            <Route
               path="/developers"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Developers />}
                  />
               }
            />
         </Route>
         <Route element={<PrivateRoute />}>
            <Route
               path="/billing"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Billing />}
                  />
               }
            />
         </Route>

         <Route element={<PrivateRoute />}>
            <Route path="/billing/success/:id" element={<BillingSuccess />} />
            <Route path="/billing/failure/:id" element={<BillingFailure />} />
         </Route>

         <Route element={<PrivateRoute />}>
            <Route path="/vendo/sticker" element={<QrSticker />} />
         </Route>

         {/* ISP Business surface. The operator's own internet service — they run
             PPPoE on their MikroTik and sell connections. Shares the login and
             the router tunnel with Piso WiFi; everything else is its own.
             
             🔴 NOT MOUNTED AT ALL unless this build enables it. The surface is in
             production DARK so staging could be handed to another session
             mid-build, and a route that still renders is a route somebody
             reaches by typing a URL. The server 404s /api/isp/* independently —
             that is the half that matters, because registering a MikroTik
             allocates from the piso-wifi tunnel plane. */}
         {ISP_ENABLED && (
         <Route element={<PrivateRoute />}>
            <Route
               path="/isp"
               element={
                  <DashboardLayout active={currentRoute} children={<IspHome />} />
               }
            />
            <Route
               path="/isp-customers"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<IspCustomers />}
                  />
               }
            />
            {/* Promoted out of Settings — the price list is edited whenever
                the business changes what it sells, not set once. */}
            <Route
               path="/isp-plans"
               element={
                  <DashboardLayout active={currentRoute} children={<IspPlans />} />
               }
            />
            <Route
               path="/isp-billing"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<IspSmartfiBilling />}
                  />
               }
            />
            <Route
               path="/isp-settings"
               element={
                  <DashboardLayout active={currentRoute} children={<IspSetup />} />
               }
            />
            {/* Still to build. A "not built yet" is the truth; the app's
                generic 404 was not. Each is replaced as it lands. */}
            <Route
               path="/isp-accounts"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<IspAccounts />}
                  />
               }
            />
            <Route
               path="/isp-servers"
               element={
                  <DashboardLayout active={currentRoute} children={<IspServers />} />
               }
            />
            <Route
               path="/isp-invoices"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<IspInvoices />}
                  />
               }
            />
            {/* /isp-proofs is gone: a payment claim now appears as "Payment
                review" on the invoice it is about (Ray, 2026-09-01). Kept as a
                redirect because operators may have bookmarked it. */}
            <Route
               path="/isp-proofs"
               element={<Navigate to="/isp-invoices" replace />}
            />
         </Route>
         )}


         {/* Piso WiFi surface (new /wifi-* URLs). */}
         <Route element={<PrivateRoute />}>
            <Route
               path="/wifi"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiDashboard />}
                  />
               }
            />
            <Route
               path="/wifi-devices"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiDevices />}
                  />
               }
            />
            <Route
               path="/wifi-networks"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiNetworks />}
                  />
               }
            />
            <Route
               path="/wifi-sold-device"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiSoldDevices />}
                  />
               }
            />
            <Route
               path="/wifi-ownership"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiOwnershipRequests />}
                  />
               }
            />
            <Route
               path="/wifi-sessions"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiSessions />}
                  />
               }
            />
            <Route
               path="/wifi-vouchers"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiVouchers />}
                  />
               }
            />
            <Route
               path="/wifi-voucher-payout"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiVoucherPayout />}
                  />
               }
            />
            <Route
               path="/wifi-settings"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<WifiSettings />}
                  />
               }
            />
            <Route
               path="/wifi-data-plans"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<DataPlans />}
                  />
               }
            />
            <Route
               path="/wifi-team"
               element={
                  <DashboardLayout active={currentRoute} children={<Team />} />
               }
            />
            <Route
               path="/wifi-partners"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Partners />}
                  />
               }
            />
            <Route
               path="/wifi-distributors"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Distributor />}
                  />
               }
            />
            <Route
               path="/wifi-resellers"
               element={
                  <DashboardLayout
                     active={currentRoute}
                     children={<Reseller />}
                  />
               }
            />
            {/* Community: a drawer on every dashboard page; on phones its own page here. */}
            <Route
               path="/wifi-community"
               element={<DashboardLayout active={currentRoute} children={<CommunityRoute />} />}
            />
            <Route
               path="/wifi-suggestions"
               element={<DashboardLayout active={currentRoute} children={<CommunityRoute view="suggestions" />} />}
            />
         </Route>

         {/* Legacy URL redirects — bookmarks and external links keep working. */}
         <Route path="/vendo" element={<Navigate to="/wifi" replace />} />
         <Route path="/data-plans" element={<Navigate to="/wifi-data-plans" replace />} />
         {/* Not a plain redirect: <Navigate to> drops the query string, and
             every partner link already shared on Facebook is
             /partners?seller=<slug>. That silent loss is exactly why the
             buyer landed on an anonymous marketplace. */}
         <Route path="/partners" element={<LegacySellerLink />} />
         <Route path="/distributor" element={<Navigate to="/wifi-distributors" replace />} />

         <Route path="*" element={<NotFound />} />
      </Routes>
   );
};

export default RoutesIndex;
